import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';

const stats = [
  { value: '128+', label: '已连载话数' },
  { value: '200+', label: '深度文章' },
  { value: '50万+', label: '月均阅读量' },
  { value: '98%', label: '读者好评率' },
];

const testimonials = [
  {
    id: '1',
    name: '漫画爱好者小李',
    content: '自从发现这个网站，我终于不用到处找秘密教学漫画的分析了。每篇剧情解析都写得很透彻，让我对作品有了更深的理解。强烈推荐给所有秘密教学漫画的粉丝！',
    rating: 5,
  },
  {
    id: '2',
    name: '资深读者阿杰',
    content: '这里的内容质量真的很高，角色分析特别精彩。以前看漫画的时候很多细节都忽略了，看了这里的分析才发现原来有这么多隐藏的伏笔。',
    rating: 5,
  },
  {
    id: '3',
    name: '新读者小美',
    content: '作为刚接触秘密教学漫画的新人，这个网站帮我快速了解了作品的全貌。分类导航很清晰，合集推荐也很有用，省去了我自己翻找的麻烦。',
    rating: 5,
  },
  {
    id: '4',
    name: '漫画评论人老张',
    content: '从专业角度来看，这里的评测文章相当有水平。不是那种泛泛而谈的影评，而是真正从画风、剧情、人物多个维度进行深入分析的干货内容。',
    rating: 5,
  },
];

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const formUrl = 'https://readdy.ai/api/form/d9slnuh4ne17ei0sdjfg';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }
    formData.delete('phone_alt');

    try {
      const response = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        if (typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam')) {
          setFormError('提交失败，请稍后重试。');
        } else {
          setFormError(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后重试。');
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试。');
      setFormStatus('error');
    }
  };

  const jsonLdOrganization = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '秘密教学漫画内容平台',
    url: 'https://www.mimijiaoxue-manhua.com',
    description: '专注于秘密教学漫画的内容导览、深度解析与粉丝社区平台',
  };

  const jsonLdAbout = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 秘密教学漫画内容平台',
    description: '了解秘密教学漫画内容平台的品牌故事、团队使命与联系方式',
    url: 'https://www.mimijiaoxue-manhua.com/about',
  };

  return (
    <>
      <SEOHead
        title="关于我们 - 秘密教学漫画 | 品牌介绍、口碑与联系方式"
        description="了解秘密教学漫画内容平台的品牌故事与团队使命。我们致力于为秘密教学漫画读者提供最全面、最专业的内容导览、深度解析与社区互动平台。"
        keywords="秘密教学漫画,关于我们,品牌介绍,联系方式,漫画平台"
        canonicalUrl="https://www.mimijiaoxue-manhua.com/about"
        ogTitle="关于我们 - 秘密教学漫画内容平台"
        ogDescription="了解秘密教学漫画内容平台的品牌故事、团队使命与联系方式。"
        ogType="website"
        ogUrl="https://www.mimijiaoxue-manhua.com/about"
      />

      <script type="application/ld+json">{JSON.stringify(jsonLdOrganization)}</script>
      <script type="application/ld+json">{JSON.stringify(jsonLdAbout)}</script>

      <Navbar />
      <ScrollToTop />

      <main className="pt-20 pb-14">
        <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
          <div className="mb-4">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>

          {/* Hero */}
          <section className="text-center mb-14">
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900">
              关于<strong>秘密教学漫画</strong>内容平台
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              我们是一群热爱《秘密教学漫画》的资深读者和内容创作者，致力于打造全网最全面、最专业的秘密教学漫画内容导览与深度解析平台。
            </p>
          </section>

          {/* Stats */}
          <section className="mb-14">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="text-center p-5 md:p-6 bg-background-100 rounded-lg border border-background-200/70"
                >
                  <div className="font-heading text-2xl md:text-3xl font-bold text-primary-500">
                    {stat.value}
                  </div>
                  <div className="mt-1 text-xs md:text-sm text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Brand Story */}
          <section className="mb-14">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-8">
              品牌故事
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=A%20warm%20and%20cozy%20creative%20team%20workspace%20with%20manga%20books%20on%20shelves%2C%20two%20people%20discussing%20over%20a%20large%20wooden%20table%20with%20laptops%20and%20notebooks%2C%20soft%20natural%20light%20from%20large%20windows%2C%20indoor%20plants%2C%20cream%20and%20warm%20amber%20color%20palette%2C%20editorial%20photography%20style%2C%20clean%20composition&width=800&height=600&seq=about-brand-story&orientation=landscape"
                  alt="秘密教学漫画内容平台团队工作场景"
                  title="秘密教学漫画 - 品牌故事"
                  className="rounded-lg w-full aspect-[4/3] object-cover"
                />
              </div>
              <div className="space-y-4">
                <h3 className="font-heading text-xl font-semibold text-foreground-900">从热爱出发</h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  秘密教学漫画内容平台诞生于2024年，由一群深爱《秘密教学漫画》的读者共同创建。我们注意到，虽然这部作品在读者中拥有广泛的影响力，但缺乏一个系统化、专业化的内容导览和分析平台。于是，我们决定自己动手，为所有秘密教学漫画的爱好者打造一个值得信赖的内容家园。
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  从最初的几篇剧情解析开始，到今天涵盖最新章节更新、角色深度介绍、专业评测和精彩合集的全方位内容平台，我们始终坚持一个信念：让每一位读者都能更深入地理解和享受《秘密教学漫画》的魅力。
                </p>
                <div className="flex items-center gap-3 pt-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                    <i className="ri-heart-line text-lg"></i>
                  </div>
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 text-accent-500">
                    <i className="ri-star-line text-lg"></i>
                  </div>
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                    <i className="ri-lightbulb-line text-lg"></i>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Testimonials */}
          <section className="mb-14 bg-background-100 rounded-lg p-6 md:p-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-2">
              读者口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              听听读者们怎么说
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="bg-background-50 rounded-lg p-5 border border-background-200/70"
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-400 text-sm"></i>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                  <p className="mt-3 text-xs text-foreground-400 font-medium">—— {t.name}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="mb-14">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 text-center mb-2">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              有任何问题或建议，欢迎随时联系我们
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-semibold text-foreground-800">电子邮箱</h4>
                    <p className="text-sm text-foreground-500">contact@mimijiaoxue-manhua.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-accent-50 text-accent-500">
                    <i className="ri-wechat-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-semibold text-foreground-800">微信公众号</h4>
                    <p className="text-sm text-foreground-500">秘密教学漫画内容平台</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
                    <i className="ri-bilibili-line text-lg"></i>
                  </div>
                  <div>
                    <h4 className="font-heading text-sm font-semibold text-foreground-800">B站账号</h4>
                    <p className="text-sm text-foreground-500">秘密教学漫画官方</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 border border-background-200/70">
                <h3 className="font-heading text-base font-semibold text-foreground-800 mb-4">
                  发送消息
                </h3>
                <form onSubmit={handleSubmit} data-readdy-form="">
                  <div className="space-y-3">
                    <div>
                      <label htmlFor="contact-name" className="block text-xs text-foreground-600 mb-1">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-name"
                        type="text"
                        name="name"
                        required
                        className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-xs text-foreground-600 mb-1">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required
                        className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-xs text-foreground-600 mb-1">
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/30 transition-colors resize-none"
                        placeholder="请输入您的留言内容..."
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>

                    <div className="anti-spam-wrapper">
                      <label htmlFor="phone_alt">备用电话</label>
                      <input
                        id="phone_alt"
                        type="text"
                        name="phone_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>

                    <button
                      type="submit"
                      className="w-full px-4 py-2.5 bg-primary-500 text-background-50 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    >
                      发送消息
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-xs text-green-600 text-center">
                        消息发送成功！我们会尽快回复您。
                      </p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-xs text-red-500 text-center">{formError}</p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
}